import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { TopNav } from '../components/TopNav';
import { Button } from '../components/Button';
import { KpiStrip, KpiTileLoading, type KpiItem } from '../components/KpiTile';
import { GenStatusPill } from '../components/StatusPill';
import { SkeletonRow } from '../components/SkeletonRow';
import { EmptyState } from '../components/EmptyState';
import { GenerateModal } from '../modals/GenerateModal';
import { listReports, downloadReport } from '../lib/api';
import { fmt } from '../lib/format';

export function Dashboard(): JSX.Element {
  const navigate = useNavigate();
  const { data: reports, isLoading, isError } = useQuery({
    queryKey: ['reports'],
    queryFn: listReports,
  });
  const [showGenerate, setShowGenerate] = useState(false);

  const latest = reports?.[0];
  const loading = isLoading;
  const empty = !loading && (!reports || reports.length === 0);
  const running = latest?.status === 'Running';
  const failed = latest?.status === 'Failed';

  const kpis: KpiItem[] = latest && latest.status === 'Succeeded' ? [
    { label: 'Sessions completed',    value: fmt.num(latest.sessions_completed) || '—', sub: 'Salesforce · completed only' },
    { label: 'Total hours incurred',  value: fmt.hrs(latest.total_hours_incurred) || '—', unit: 'h', sub: 'across all sessions' },
    { label: 'Total fees budgeted',   value: fmt.curK(latest.total_fee_budget), sub: 'sum of client fee budget' },
    { label: 'Active engagements',    value: fmt.num(latest.active_engagements) || '—', sub: 'distinct opportunities' },
  ] : [];

  return (
    <div className="ari-app">
      <TopNav />
      <div className="page">
        <div className="page-hd">
          <div>
            <div className="eb">Latest report</div>
            <h1 className="d-title">
              {latest ? `Week of ${fmt.range(latest.rangeStart, latest.rangeEnd)}` : 'No reports yet'}
            </h1>
            {latest && (
              <div className="d-meta" style={{ marginTop: 8, fontSize: 13 }}>
                Generated <span className="d-tnum">{fmt.dateTime(latest.generatedAt)}</span> by {latest.user}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Button variant="secondary" icon="plus" onClick={() => setShowGenerate(true)}>
              Generate new report
            </Button>
            {latest && latest.status === 'Succeeded' && (
              <Button variant="primary" icon="download" onClick={() => downloadReport(latest.id)}>
                Download .xlsx
              </Button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="kpi-strip">
            {[0, 1, 2, 3].map((i) => <KpiTileLoading key={i} />)}
          </div>
        ) : running ? (
          <div className="kpi-strip">
            {['Sessions completed', 'Total hours incurred', 'Total fees budgeted', 'Active engagements'].map((label) => (
              <div className="kpi" key={label}>
                <div className="kpi-l">{label}</div>
                <div className="kpi-v">—</div>
              </div>
            ))}
          </div>
        ) : failed ? null : kpis.length ? (
          <KpiStrip items={kpis} />
        ) : null}

        {running && latest && (
          <div style={{ padding: '16px var(--d-s7) 0' }}>
            <div className="banner warn">
              <span className="dot" />
              <span>
                <strong>Generation in progress.</strong> Started {fmt.dateTime(latest.generatedAt)} · usually ~10s.
              </span>
              <div style={{ flex: 1 }} />
              <div style={{ width: 180 }}><div className="progbar" /></div>
            </div>
          </div>
        )}
        {failed && (
          <div style={{ padding: '16px var(--d-s7) 0' }}>
            <div className="banner err">
              <span className="dot" />
              <span><strong>Last generation failed.</strong> Check the error log for details.</span>
              <div style={{ flex: 1 }} />
              <Button size="sm" variant="ghost" icon="eye">View error log</Button>
              <Button size="sm" variant="secondary" icon="refresh" onClick={() => setShowGenerate(true)}>Retry</Button>
            </div>
          </div>
        )}

        <div style={{ padding: 'var(--d-s8) var(--d-s7)', flex: 1, overflow: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 20 }}>
            <div>
              <div className="eb" style={{ color: 'var(--mute)' }}>Recent generations</div>
              <h2 className="d-h2" style={{ marginTop: 6 }}>Last 10 runs</h2>
            </div>
            <a className="btn-link" onClick={() => navigate('/reports')}>View all reports →</a>
          </div>

          {empty ? (
            <div className="card">
              <EmptyState
                icon="barchart"
                title="No reports yet"
                body="Generate your first report to pull this week's Salesforce data. It usually takes ~10 seconds."
                cta={<Button variant="primary" icon="plus" onClick={() => setShowGenerate(true)}>Generate first report</Button>}
              />
            </div>
          ) : loading ? (
            <div className="card" style={{ padding: 16 }}>
              <table className="tbl gen">
                <thead>
                  <tr><th>Generated</th><th>Range</th><th className="num">Rows</th><th>By</th><th>Status</th><th /></tr>
                </thead>
                <tbody>
                  {Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} cells={6} />)}
                </tbody>
              </table>
            </div>
          ) : isError ? (
            <div className="banner err">
              <span className="dot" />
              <span>Failed to load report history. Try refreshing.</span>
            </div>
          ) : (
            <div className="card">
              <table className="tbl gen">
                <thead>
                  <tr>
                    <th>Generated</th>
                    <th>Range covered</th>
                    <th className="num">Rows</th>
                    <th>By</th>
                    <th>Status</th>
                    <th style={{ width: 1 }} />
                  </tr>
                </thead>
                <tbody>
                  {reports!.slice(0, 10).map((g) => (
                    <tr key={g.id} onClick={() => g.status === 'Succeeded' && navigate(`/reports/${g.id}`)}>
                      <td className="d-tnum">{fmt.dateTime(g.generatedAt)}</td>
                      <td>{fmt.range(g.rangeStart, g.rangeEnd)}</td>
                      <td className="num">{g.rows ? fmt.num(g.rows) : <span className="em">—</span>}</td>
                      <td>{g.user}</td>
                      <td><GenStatusPill value={g.status} /></td>
                      <td style={{ paddingRight: 16 }}>
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                          {g.status === 'Succeeded' && (
                            <>
                              <Button size="sm" variant="ghost" icon="eye" onClick={(e) => { e.stopPropagation(); navigate(`/reports/${g.id}`); }}>View</Button>
                              <Button size="sm" variant="ghost" icon="download" onClick={(e) => { e.stopPropagation(); downloadReport(g.id); }}>.xlsx</Button>
                            </>
                          )}
                          {g.status === 'Failed' && (
                            <Button size="sm" variant="ghost" icon="alert">Error log</Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showGenerate && (
        <GenerateModal
          onClose={() => setShowGenerate(false)}
          onGenerated={(id) => navigate(`/reports/${id}`)}
        />
      )}
    </div>
  );
}
